import React, { useMemo } from 'react';
import { Plus, Minus, Lock } from 'lucide-react';
import { Authority } from './AuthorityTypes';
import NoDataFound from '../common/NoDataFound';
import ApprovalDot from '../common/ApprovalDot';
import Badge from '../common/Badge';

interface AuthorityListProps {
    authorities: Authority[];
    loading: boolean;
    /** Row click opens the read-only detail modal; editing is reached from its header. */
    onView: (authority: Authority) => void;
    expandedIds: Set<number>;
    onToggleExpand: (id: number) => void;
    onWorkflow?: (authority: Authority) => void;
}

const AuthorityList: React.FC<AuthorityListProps> = ({ authorities, loading, onView, expandedIds, onToggleExpand, onWorkflow }) => {
    const treeData = useMemo(() => {
        const buildTree = (parentId: number | null): (Authority & { children?: any[] })[] => {
            return authorities
                .filter(item => item.parent_authority_id === parentId)
                .map(item => ({
                    ...item,
                    children: buildTree(item.id)
                }));
        };
        return buildTree(null);
    }, [authorities]);

    const toggleExpand = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        onToggleExpand(id);
    };

    const AuthorityRow = ({ node, level = 0 }: { node: Authority & { children?: any[] }, level?: number }) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedIds.has(node.id);
        const paddingLeft = level * 24 + 12;

        return (
            <React.Fragment>
                <div
                    onClick={() => onView(node)}
                    data-testid={`BTN_authority_row_${node.id}_open`}
                    className={`tree-list-row${level > 0 ? ' tree-list-row--child' : ''}${node.is_active !== 1 ? ' is-inactive' : ''}`}
                >
                    <div
                        className="tree-list-cell tree-list-cell--dot"
                        onClick={(e) => { e.stopPropagation(); onWorkflow?.(node); }}
                        title="Workflow — view timeline & act"
                        data-testid={`BTN_authority_row_${node.id}_workflow`}
                    >
                        <ApprovalDot
                            state={node.workflow_state ?? 'draft'}
                            onClick={(e) => { e.stopPropagation(); onWorkflow?.(node); }}
                        />
                    </div>

                    <div className="tree-list-cell tree-list-cell--w-40">
                        {node.code ? (
                            <span className="cell-code" data-testid={`CELL_authority_code_${node.id}`}>
                                {node.code}
                            </span>
                        ) : (
                            <span className="text-gray-400">—</span>
                        )}
                    </div>

                    <div
                        className="tree-list-cell tree-list-cell--main"
                        style={{ paddingLeft: `${paddingLeft}px` }}
                    >
                        <button
                            onClick={(e) => toggleExpand(e, node.id)}
                            data-testid={`BTN_authority_row_${node.id}_toggle`}
                            className={`tree-list-toggle${hasChildren ? '' : ' tree-list-toggle--hidden'}`}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                            {isExpanded ? <Minus size={10} /> : <Plus size={10} />}
                        </button>

                        <div className="tree-list-row-title">
                            <span className="tree-list-row-name cell-name-lock">
                                {node.workflow_state === 'approved' && (
                                    <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                )}
                                {node.name}
                            </span>
                            {node.description && (
                                <span className="tree-list-row-sub">{node.description}</span>
                            )}
                        </div>
                    </div>

                    <div className="tree-list-cell tree-list-cell--w-48">
                        <Badge variant={node.is_physical ? 'info' : 'success'} size="sm">
                            {node.is_physical ? 'Physical' : 'Logical'}
                        </Badge>
                    </div>

                </div>

                <div className={`tree-list-children${isExpanded ? ' tree-list-children--open' : ''}`}>
                    <div className="tree-list-children-inner">
                        {hasChildren && node.children?.map(child => (
                            <AuthorityRow key={child.id} node={child} level={level + 1} />
                        ))}
                    </div>
                </div>
            </React.Fragment>
        );
    };

    return (
        <div className="tree-list">
            <div className="tree-list-header">
                <div className="tree-list-header-cell tree-list-cell--dot" aria-label="Workflow status" />
                <div className="tree-list-header-cell tree-list-cell--w-40">Code</div>
                <div className="tree-list-header-cell tree-list-header-cell--main">Authority Structure</div>
                <div className="tree-list-header-cell tree-list-cell--w-48">Type</div>
            </div>

            <div className="tree-list-body">
                {loading ? (
                    <div className="tree-list-body-inner" aria-busy="true" aria-live="polite">
                        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                            <div key={i} className="tree-list-row tree-list-skeleton-row">
                                <div className="tree-list-cell tree-list-cell--dot">
                                    <div className="table-list-skeleton-bar skel-dot" />
                                </div>
                                <div className="tree-list-cell tree-list-cell--w-40">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--code" />
                                </div>
                                <div
                                    className="tree-list-cell tree-list-cell--main"
                                    style={{ paddingLeft: `${(i % 3 === 1 ? 36 : 12)}px` }}
                                >
                                    <div className="tree-list-skeleton-toggle" />
                                    <div className="tree-list-row-title">
                                        <div className="tree-list-skeleton-bar tree-list-skeleton-bar--title" />
                                        <div className="tree-list-skeleton-bar tree-list-skeleton-bar--sub" />
                                    </div>
                                </div>
                                <div className="tree-list-cell tree-list-cell--w-48">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--pill" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : authorities.length === 0 ? (
                    <NoDataFound
                        title="No Authority Found"
                        message="No authorities have been defined yet."
                    />
                ) : (
                    <div className="tree-list-body-inner">
                        {treeData.map(node => (
                            <AuthorityRow key={node.id} node={node} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuthorityList;
